<script setup>
// Inspector do nó "Agendar reunião": liga/desliga + escolhe EM QUAL agenda
// (usuário com Google Agenda conectado) o agente cria os eventos. Sem escolha,
// usa o 1º usuário conectado da conta. Guarda em data.calendarUserId.
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import calendarUsersAPI from 'dashboard/api/agents/calendarUsers';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const store = useStore();

const users = ref([]);
const loading = ref(true);

const desc = computed(() =>
  t('AI_AGENTS.FLOW.TOOLS.tool_schedule_meeting.DESC')
);

// Tipo da reunião fixado pelo nó ('' = automático: LLM/conta decidem)
const provider = computed(() => props.data.meetingProvider || '');

// Quem ATENDE a reunião. Aparece em todos os tipos: mesmo numa chamada do
// ChatSuite ou numa reunião presencial, alguém precisa ser o dono — sem isso o
// backend cai em `@account.users.first`, um usuário arbitrário por id.
// Nó salvo antes de calendarMode existir: infere pelo calendarUserId.
const calendarMode = computed(
  () => props.data.calendarMode || (props.data.calendarUserId ? 'user' : 'auto')
);
const calendarUserIds = computed(() =>
  (props.data.calendarUserIds || []).map(Number)
);
const isRoundRobin = computed(() => calendarMode.value === 'round_robin');
// Menos de dois não reveza: o backend cai no caminho de pessoa única.
const tooFewForRotation = computed(
  () => isRoundRobin.value && calendarUserIds.value.length < 2
);

const setCalendarMode = event => {
  const value = event.target.value;
  // troca de modo limpa a chave do outro, pra não sobrar destino ambíguo
  const patch = { calendarMode: value };
  if (value !== 'user') patch.calendarUserId = null;
  if (value !== 'round_robin') patch.calendarUserIds = [];
  emit('update', patch);
};

const toggleCalendarUser = (id, checked) => {
  const next = calendarUserIds.value.filter(x => x !== Number(id));
  if (checked) next.push(Number(id));
  emit('update', { calendarUserIds: next });
};

// Pessoas da empresa avisadas quando o agente agendar (viram convidados do
// evento → convite + lembrete pelo pipeline da Agenda)
const accountAgents = computed(() => store.getters['agents/getAgents'] || []);
const notifyUsers = computed(() => props.data.notifyUsers || []);

const isSelected = id => notifyUsers.value.some(u => u.userId === id);

const toggleUser = agent => {
  // WhatsApp do perfil (Perfil → WhatsApp): cadastrado 1× e reusado aqui —
  // com telefone, o aviso já sai por email + WhatsApp sem digitar nada.
  const profilePhone = agent.custom_attributes?.phone_number || '';
  const next = isSelected(agent.id)
    ? notifyUsers.value.filter(u => u.userId !== agent.id)
    : [
        ...notifyUsers.value,
        {
          userId: agent.id,
          name: agent.name,
          email: agent.email,
          phone: profilePhone,
          via: profilePhone ? ['email', 'whatsapp'] : ['email'],
        },
      ];
  emit('update', { notifyUsers: next });
};

const patchUser = (id, patch) =>
  emit('update', {
    notifyUsers: notifyUsers.value.map(u =>
      u.userId === id ? { ...u, ...patch } : u
    ),
  });

const toggleVia = (user, channel) => {
  const via = user.via.includes(channel)
    ? user.via.filter(v => v !== channel)
    : [...user.via, channel];
  patchUser(user.userId, { via });
};

onMounted(async () => {
  store.dispatch('agents/get');
  try {
    const { data } = await calendarUsersAPI.connectedUsers();
    users.value = data.users || [];
  } catch {
    users.value = [];
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
        >
          <span class="i-lucide-calendar-clock text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.MEETING.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">{{ desc }}</p>
    </div>

    <!-- Liga/desliga -->
    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
      </span>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#4285f4]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <!-- Tipo da reunião (fixado pelo nó) -->
    <label class="mb-1 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.MEETING.PROVIDER') }}
    </label>
    <select
      :value="provider"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="emit('update', { meetingProvider: $event.target.value || null })"
    >
      <option value="">{{ t('AI_AGENTS.FLOW.MEETING.PROVIDER_AUTO') }}</option>
      <option value="chatsuite">
        {{ t('AI_AGENTS.FLOW.MEETING.PROVIDER_CHATSUITE') }}
      </option>
      <option value="google_meet">
        {{ t('AI_AGENTS.FLOW.MEETING.PROVIDER_GOOGLE') }}
      </option>
      <option value="presential">
        {{ t('AI_AGENTS.FLOW.MEETING.PROVIDER_PRESENTIAL') }}
      </option>
    </select>

    <!-- Quem atende: aparece em TODOS os tipos de reunião -->
    <label class="mb-1 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR_MODE') }}
    </label>
    <select
      :value="calendarMode"
      data-testid="meeting-calendar-mode"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      @change="setCalendarMode"
    >
      <option value="auto">
        {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR_MODE_AUTO') }}
      </option>
      <option value="user">
        {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR_MODE_USER') }}
      </option>
      <option value="round_robin">
        {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR_MODE_ROUND_ROBIN') }}
      </option>
    </select>

    <template v-if="calendarMode === 'user'">
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR') }}
      </label>
      <select
        :value="data.calendarUserId || ''"
        data-testid="meeting-calendar-user"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @change="
          emit('update', {
            calendarUserId: Number($event.target.value) || null,
          })
        "
      >
        <option value="">
          {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR_AUTO') }}
        </option>
        <option v-for="u in users" :key="u.id" :value="u.id">
          {{ u.name }} ({{ u.email }})
        </option>
      </select>
    </template>

    <template v-else-if="isRoundRobin">
      <div class="mb-1 flex items-center justify-between">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.MEETING.CALENDAR_MEMBERS') }}
        </span>
        <span class="text-xs tabular-nums text-n-slate-10">
          {{ calendarUserIds.length }}/{{ users.length }}
        </span>
      </div>
      <ul class="max-h-52 overflow-y-auto">
        <li v-for="u in users" :key="u.id" class="flex items-center gap-2 py-1">
          <input
            :id="`meeting-member-${u.id}`"
            type="checkbox"
            :data-testid="`meeting-member-${u.id}`"
            :checked="calendarUserIds.includes(Number(u.id))"
            class="size-4 shrink-0 rounded border-n-weak accent-[#4285f4]"
            @change="toggleCalendarUser(u.id, $event.target.checked)"
          />
          <label
            :for="`meeting-member-${u.id}`"
            class="text-sm text-n-slate-12"
          >
            {{ u.name }} ({{ u.email }})
          </label>
        </li>
      </ul>
      <p
        v-if="tooFewForRotation"
        data-testid="meeting-too-few"
        class="mt-2 flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
      >
        <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
        <span>{{
          t('AI_AGENTS.FLOW.MEETING.CALENDAR_ROUND_ROBIN_TOO_FEW')
        }}</span>
      </p>
    </template>

    <p
      v-if="!loading && !users.length"
      data-testid="meeting-none-connected"
      class="mt-2 flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11"
    >
      <span class="i-lucide-alert-triangle mt-0.5 shrink-0" />
      <span>{{ t('AI_AGENTS.FLOW.MEETING.NONE_CONNECTED') }}</span>
    </p>
    <p v-else class="mt-1 text-xs text-n-slate-10">
      {{
        isRoundRobin
          ? t('AI_AGENTS.FLOW.MEETING.CALENDAR_ROUND_ROBIN_HINT')
          : t('AI_AGENTS.FLOW.MEETING.CALENDAR_HINT')
      }}
    </p>

    <!-- Avisar pessoas da empresa (convite + lembrete) -->
    <div class="mt-4">
      <label class="mb-1 block text-xs font-medium text-n-slate-11">
        {{ t('AI_AGENTS.FLOW.MEETING.NOTIFY') }}
      </label>
      <p class="mb-2 text-xs text-n-slate-10">
        {{ t('AI_AGENTS.FLOW.MEETING.NOTIFY_HINT') }}
      </p>
      <div class="mb-2 flex flex-wrap gap-1.5">
        <button
          v-for="agentUser in accountAgents"
          :key="agentUser.id"
          type="button"
          class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
          :class="
            isSelected(agentUser.id)
              ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
              : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
          "
          @click="toggleUser(agentUser)"
        >
          {{ agentUser.name }}
        </button>
      </div>
      <div
        v-for="user in notifyUsers"
        :key="user.userId"
        class="mb-2 rounded-lg border border-n-weak bg-n-solid-1 p-2.5"
      >
        <div class="mb-1.5 flex items-center justify-between gap-2">
          <span class="truncate text-xs font-medium text-n-slate-12">
            {{ user.name }}
          </span>
          <span class="truncate text-[11px] text-n-slate-10">
            {{ user.email }}
          </span>
        </div>
        <div class="flex items-center gap-1.5">
          <button
            type="button"
            class="rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors"
            :class="
              user.via.includes('email')
                ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                : 'border-n-weak text-n-slate-10'
            "
            @click="toggleVia(user, 'email')"
          >
            {{ t('AI_AGENTS.FLOW.MEETING.VIA_EMAIL') }}
          </button>
          <button
            type="button"
            class="rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors"
            :class="
              user.via.includes('whatsapp')
                ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                : 'border-n-weak text-n-slate-10'
            "
            @click="toggleVia(user, 'whatsapp')"
          >
            {{ t('AI_AGENTS.FLOW.MEETING.VIA_WHATSAPP') }}
          </button>
          <input
            v-if="user.via.includes('whatsapp')"
            :value="user.phone"
            type="text"
            class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1 text-xs text-n-slate-12"
            :placeholder="t('AI_AGENTS.FLOW.MEETING.NOTIFY_PHONE_PH')"
            @change="patchUser(user.userId, { phone: $event.target.value })"
          />
        </div>
      </div>
    </div>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
